import { ChangeDetectorRef, Component, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';

import { Store } from '@ngrx/store';

import { combineLatest, Observable, Subscription } from 'rxjs';

import { AppState } from 'src/app/state';
import { PrivilegeService } from '../../../services/privilege/privilege.service';
import {
  deleteIntervention,
  duplicateIntervention,
  getIntervention,
  resetStartModuleSetFlag,
  setSelectedIntervention,
} from '../../../state/intervention/intervention.actions';
import { openDashboardConfirmationDialog } from '../../../state/dashboard.actions';
import { getDevices } from '../../../state/device/device.actions';
import { selectSelectedStudy } from '../../../state/study/study.selectors';
import {
  selectInterventions,
  selectInterventionsLoaded,
  selectIsLoadingInterventions,
  selectSelectedIntervention,
  selectStartModuleSet,
} from '../../../state/intervention/intervention.selectors';
import { Study } from 'src/app/models/study';
import { Role } from '../../../../models/role';
import { Intervention } from 'src/app/models/intervention';
import { StudyType } from 'src/app/models/study-type';
import { PRIVILEGE_ACTIONS, PRIVILEGE_SECTIONS } from '../../../../core/dashboard-constants';
import { sortByDate } from '../../helpers/date-helper';
import {
  selectContentItemsSortingOrder,
  selectInterventionContentItems,
} from '../../../state/intervention-content-item/intervention-content-item.selectors';
import { InterventionContentItem } from '../../../../models/intervention-content-item';
import { InterventionService } from '../../../services/intervention/intervention.service';
import { DashboardCamelCasePipe } from '../../../../core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { ESMTriggerRule } from '../../../../models/trigger';
import { Module } from '../../../../models/module';
import { selectModules } from '../../../state/module/module.selectors';
import { reorderInterventionContentItems } from '../../helpers/intervention-helper';
import { NgZone } from '@angular/core';

interface SortOption {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'dashboard-intervention',
  templateUrl: './interventions.component.html',
  styleUrls: ['./interventions.component.scss'],
})
export class InterventionsComponent implements OnInit, OnDestroy {
  @ViewChild('tabs') tabGroup: MatTabGroup;

  interventionTitleLimitation = 35;
  interventionDescriptionLimitation = 50;
  intervention: Intervention;

  Role = Role;
  StudyType = StudyType;
  isEdit = false;
  isOverview = false;
  openOverviewCalender = false;
  loading = false;
  esmRules: ESMTriggerRule[] = [];
  esmModuleNames: any[] = [];
  modules: Module[] = [];
  interventionContentItems: InterventionContentItem[];
  selectModuleList = [];

  selectedStudy$: Observable<Study>;
  selectedStudy: Study;

  selectedIntervention: Intervention;
  selectedInterventionContentItems: InterventionContentItem[];

  startModuleSet: boolean;

  loadingInterventions$: Observable<boolean>;
  interventionsLoaded$: Observable<boolean>;

  allInterventions: Intervention[];
  sortedInterventions: Intervention[];
  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;
  private subscriptions = new Subscription();

  sortOptions: SortOption[] = [
    { value: 'showAll', viewValue: 'All interventions' },
    { value: 'showAtoZ', viewValue: 'A to Z' },
    { value: 'showZtoA', viewValue: 'Z to A' },
    { value: 'latest', viewValue: 'Latest created' },
  ];

  searchInterventions: string;
  selectedOption: string;

  constructor(
    private store: Store<AppState>,
    public privilegeService: PrivilegeService,
    private interventionService: InterventionService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private zone: NgZone,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadingInterventions$ = this.store.select(selectIsLoadingInterventions);
    this.interventionsLoaded$ = this.store.select(selectInterventionsLoaded);
    this.subscriptions.add(
      this.store.select(selectInterventions).subscribe((interventions) => {
        this.allInterventions = interventions;
        for (let i = 0; i < this.allInterventions.length; i++) {
          this.interventionService
            .getIntervention(this.allInterventions[i].studyId, this.allInterventions[i].id)
            .subscribe((data) => {
              this.allInterventions[i] = this.dashboardCamelCasePipe.keysToCamelCase(data);
            });
        }
        this.sortedInterventions = this.allInterventions;
      })
    );

    this.subscriptions.add(
      this.store.select(selectSelectedIntervention).subscribe((intervention) => {
        if (intervention) {
          this.isEdit = true;
          this.selectedIntervention = intervention;
        } else {
          this.isEdit = false;
        }
      })
    );

    this.subscriptions.add(
      this.store.select(selectInterventionContentItems).subscribe((interventionContentItems) => {
        this.selectedInterventionContentItems = interventionContentItems;
      })
    );

    this.subscriptions.add(
      this.store.select(selectSelectedStudy).subscribe((selectedStudy) => (this.selectedStudy = selectedStudy))
    );

    this.subscriptions.add(
      this.store.select(selectStartModuleSet).subscribe((startModuleSet) => {
        if (startModuleSet) {
          this.startModuleSet = startModuleSet;
          this.store.dispatch(resetStartModuleSetFlag());
        }
      })
    );

    this.subscriptions.add(
      combineLatest([
        this.store.select(selectModules),
        this.store.select(selectInterventionContentItems),
        this.store.select(selectContentItemsSortingOrder),
      ]).subscribe(([modules, interventionContentItems, contentItemsSortingOrder]) => {
        this.interventionContentItems = reorderInterventionContentItems(
          contentItemsSortingOrder,
          interventionContentItems
        );
        this.modules = modules.filter((module) => !module.enrolment);
        this.createSelectModuleList();
        this.setupTriggers();
      })
    );
  }

  createSelectModuleList(): void {
    this.selectModuleList = this.modules.filter(
      (module) =>
        !this.interventionContentItems.find((moduleToBeRemoved) => moduleToBeRemoved.module?.id === module?.id)
    );
  }

  setupTriggers(): void {
    this.loading = true;
    this.esmRules = [];
    for (const interventionContentItem of this.interventionContentItems) {
      if (interventionContentItem.triggers) {
        if (interventionContentItem.triggers.length > 0) {
          for (const trigger of interventionContentItem.triggers) {
            this.esmModuleNames = this.esmModuleNames.concat([
              { name: interventionContentItem.module.name, id: trigger.id },
            ]);
            if (trigger.esmRules && trigger.esmRules.length > 0) {
              this.zone.run(() => {
                this.esmRules = this.esmRules.concat(trigger.esmRules);
              });
            }
          }
        } else {
          this.esmModuleNames = this.esmModuleNames.concat([{ name: interventionContentItem.module.name, id: null }]);
        }
      }
    }

    this.isOverview = this.openOverviewCalender;

    this.loading = false;
    this.cd.detectChanges();
  }

  onNewIntervention(): void {
    this.isEdit = false;
    this.startModuleSet = false;
    this.store.dispatch(setSelectedIntervention({ selectedIntervention: null }));
  }

  openInterventionDetails(intervention: Intervention): void {
    for (const module of intervention.modulePivots) {
      for (const trigger of module.triggers) {
        if (trigger.esmRules.length > 0) {
          this.openOverviewCalender = true;
        }
      }
    }

    this.isEdit = true;
    if (this.tabGroup) {
      this.tabGroup.selectedIndex = 0;
      this.startModuleSet = false;
    }
    this.store.dispatch(
      getIntervention({
        studyId: intervention.studyId,
        interventionId: intervention.id,
      })
    );
  }

  openDeleteDialog(intervention: Intervention): void {
    this.store.dispatch(
      openDashboardConfirmationDialog({
        dialogData: {
          title: `Are you sure you want to delete this intervention?`,
          onConfirm: deleteIntervention({ intervention }),
        },
      })
    );
  }

  duplicateIntervention(intervention: Intervention): void {
    this.startModuleSet = false;
    this.store.dispatch(duplicateIntervention({ intervention }));
  }

  tabChanged(tabChangeEvent: MatTabChangeEvent): void {
    if (tabChangeEvent.index === 2) {
      this.store.dispatch(getDevices({ intervention: this.selectedIntervention }));
    }
  }

  backToOverview(): void {
    this.isOverview = false;
    this.openOverviewCalender = false;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  sort(value: string): void {
    this.selectedOption = value;
    switch (value) {
      case 'showAll':
        this.showAllInterventions();
        break;
      case 'showAtoZ':
        this.sortAlphabetically();
        break;
      case 'showZtoA':
        this.sortReversedAlphabetically();
        break;
      case 'latest':
        this.sortByDateCreated();
        break;
    }
  }

  filterInterventions(): void {
    if (this.searchInterventions !== '') {
      this.sort(!this.selectedOption ? 'showAll' : this.selectedOption);
      this.sortedInterventions = this.sortedInterventions.filter((intervention) =>
        intervention.name?.toLowerCase().includes(this.searchInterventions.toLowerCase())
      );
    } else {
      this.sort(this.selectedOption);
    }
  }

  private showAllInterventions(): void {
    this.sortByDateCreated();
  }

  private sortAlphabetically(): void {
    this.sortedInterventions = this.allInterventions;
    this.sortedInterventions.sort((intervention1, intervention2) =>
      intervention1.name.localeCompare(intervention2.name)
    );
  }

  private sortReversedAlphabetically(): void {
    this.sortedInterventions = this.allInterventions;
    this.sortedInterventions.sort((intervention1, intervention2) =>
      intervention2.name.localeCompare(intervention1.name)
    );
  }

  private sortByDateCreated(): void {
    this.sortedInterventions = this.allInterventions;
    this.sortedInterventions.sort((intervention1, intervention2) =>
      sortByDate(intervention1.createdAt, intervention2.createdAt)
    );
  }
}
